import { useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { useChatMensagemToast } from '@/features/chat/useChatMensagemToast';
import { useChatResumo } from '@/features/chat/useChatQueries';
import { useNotificacaoRealtime } from '@/features/notificacoes/useNotificacaoRealtime';
import { PushAtivarBanner } from '@/features/push/PushAtivarBanner';
import { usePushNotifications } from '@/features/push/usePushNotifications';
import { relatoriosVisiveis } from '@/features/relatorios/relatoriosCatalogo';
import { buildNavItems } from '@/features/shell/buildNavItems';
import { MegaMenu } from '@/features/shell/MegaMenu';
import type { MenuBuscaItem } from '@/features/shell/navCatalog';
import { useNavAtalhos } from '@/features/shell/useNavAtalhos';
import { useSidebarRetraida } from '@/features/shell/useSidebarRetraida';
import { AssistenteProvider } from '@/features/assistente/assistenteUi';
import { AssistenteWidget } from '@/features/assistente/AssistenteWidget';
import { RealtimeProvider } from '@/shared/lib/realtime/RealtimeProvider';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import styles from './AppShell.module.css';

type AppShellProps = {
  children: ReactNode;
};

export function AppShell({ children }: AppShellProps) {
  const { user, patchAtalhos } = useAuth();
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const { retraida, toggleRetraida } = useSidebarRetraida();

  const { precisaAtivar, permissaoBloqueada, ativando, ativar } = usePushNotifications({
    enabled: true,
  });

  // Badge: polling leve; a pagina /chat invalida ao receber eventos WS.
  const resumoQuery = useChatResumo(true);
  const chatBadge = resumoQuery.data?.totalNaoLidas ?? 0;

  const allNavItems = useMemo(() => {
    const items = buildNavItems(user?.acoes ?? []);
    return items.map((item) =>
      item.codigoModulo === 230 && chatBadge > 0 ? { ...item, badge: chatBadge } : item,
    );
  }, [user?.acoes, chatBadge]);

  const itensBuscaExtras = useMemo<MenuBuscaItem[]>(
    () =>
      relatoriosVisiveis(user?.acoes ?? [], '').map((relatorio) => ({
        id: `relatorio-${relatorio.id}`,
        titulo: relatorio.titulo,
        descricao: relatorio.descricao,
        categoria: 'Relatórios',
        path: relatorio.path,
        icon: relatorio.icon,
      })),
    [user?.acoes],
  );

  const { sidebarItems, isFavorito, toggleFavorito, reordenarAtalhos } = useNavAtalhos({
    idUsuario: user?.idUsuario,
    idEscola: user?.idEscolaAtiva,
    atalhosFromApi: user?.atalhos,
    allItems: allNavItems,
    onAtalhosChange: patchAtalhos,
  });

  return (
    <RealtimeProvider enabled={Boolean(user)} chaveSessao={user?.idEscolaAtiva ?? null}>
      <AssistenteProvider>
      <AvisosRealtime />
      <div
        className={styles.shell}
        data-sidebar-retraida={retraida ? 'true' : 'false'}
      >
        <Header
          onOpenMegaMenu={() => setMegaMenuOpen(true)}
          onCloseMegaMenu={() => setMegaMenuOpen(false)}
          megaMenuAberto={megaMenuOpen}
          sidebarRetraida={retraida}
        />
        {precisaAtivar ? (
          <PushAtivarBanner
            bloqueada={permissaoBloqueada}
            ativando={ativando}
            onAtivar={() => void ativar()}
          />
        ) : null}
        <MegaMenu
          open={megaMenuOpen}
          onClose={() => setMegaMenuOpen(false)}
          items={allNavItems}
          itensBuscaExtras={itensBuscaExtras}
          isFavorito={isFavorito}
          onToggleFavorito={toggleFavorito}
        />
        <div className={styles.body}>
          <Sidebar
            items={sidebarItems}
            retraida={retraida}
            onToggleRetraida={toggleRetraida}
            onReordenar={reordenarAtalhos}
            onRemoverAtalho={toggleFavorito}
          />
          <main className={styles.main}>{children}</main>
        </div>
        <AssistenteWidget />
      </div>
      </AssistenteProvider>
    </RealtimeProvider>
  );
}

/** Toasts de aviso e de chat. Componente separado para os hooks viverem dentro do provider. */
function AvisosRealtime() {
  useNotificacaoRealtime();
  useChatMensagemToast();
  return null;
}
